import { doAdminMutation, IQueryInfo } from 'infrastructure/api'
import {
  CrosswalkMappingSetType,
  FrameworkType,
  StandardAlignmentInputType,
  StandardAlignmentResponseType,
  TreeCreateUnit,
} from './types'
import { sharedQueryFields } from 'shared/api/utility'
import { makeContentServiceFlowRequest } from 'infrastructure/contentService'

async function getAllResource<T>(resourceKey: keyof typeof sharedQueryFields) {
  const queryInfo: IQueryInfo = {
    resourceData: {
      key: resourceKey,
      fields: sharedQueryFields[resourceKey],
    },
  }
  const result = await doAdminMutation<T[]>(
    queryInfo.resourceData,
    undefined,
    false,
  )
  const error = result.errors?.map(({ message }) => message).join('\n')
  return {
    result: error ? [] : result.data,
    error,
  }
}

export function getAllFrameworks() {
  return getAllResource<FrameworkType>('frameworks')
}

export function getAllCrosswalks() {
  return getAllResource<CrosswalkMappingSetType>('crosswalkMappingSets')
}

export async function getCrosswalkById(crosswalkId: number) {
  const result = await doAdminMutation<CrosswalkMappingSetType>(
    {
      key: 'crosswalkMappingSet',
      fields: sharedQueryFields.crosswalkMappingSet,
    },
    { crosswalk_id: crosswalkId },
    false,
  )
  const error = result.errors?.map(({ message }) => message).join('\n')
  return { result: error ? undefined : result.data, error }
}

export async function getCrosswalkByIds(params: {
  source_id: number
  target_id: number
}) {
  const result = await doAdminMutation<CrosswalkMappingSetType[]>(
    {
      key: 'crosswalkMappingSetsForSourceTarget',
      fields: sharedQueryFields.crosswalkMappingSets,
    },
    params,
    false,
  )
  const error = result.errors?.map(({ message }) => message).join('\n')
  return { result: error ? [] : result.data, error }
}

export async function createCrosswalkMappingSet(
  data: Omit<CrosswalkMappingSetType, 'id'>,
) {
  const queryInfo: IQueryInfo = {
    resourceData: {
      key: 'addCrosswalkMappingSet',
    },
    params: {
      ...data,
    },
  }
  const result = await doAdminMutation(
    queryInfo.resourceData,
    queryInfo.params,
    false,
  )
  const error = result.errors?.map(({ message }) => message).join('\n')
  return { error }
}

export async function maintainCrosswalkAlignments(
  crosswalk_id: number,
  alignments: StandardAlignmentInputType[],
) {
  const queryInfo: IQueryInfo = {
    resourceData: {
      key: 'maintainCrosswalkAlignments',
    },
    params: {
      crosswalk_id,
      alignments,
    },
  }
  const result = await doAdminMutation(
    queryInfo.resourceData,
    queryInfo.params,
    false,
  )
  const error = result.errors?.map(({ message }) => message).join('\n')
  return { error }
}

export async function getAlignmentsForMappingId(mapping_set_id: number) {
  const result = await doAdminMutation<StandardAlignmentResponseType[]>(
    {
      key: 'standardAlignmentsForMappingSetID',
      fields: sharedQueryFields.standardAlignments,
    },
    { mapping_set_id },
    false,
  )
  const error = result.errors?.map(({ message }) => message).join('\n')
  return { result: result.data ?? [], error }
}

export async function createTreeStructure(
  grade: number,
  trees: { tree_id: string; version_id: string }[],
  units: { name: string; standards: string[] }[],
) {
  const path = '/content/v1/scope_and_sequence/tree_structure'
  return makeContentServiceFlowRequest(path, { grade, trees, units })
}

export async function createTree(
  grade: number,
  units: TreeCreateUnit[],
  title: string,
  subject_id?: number,
  attribution?: string,
  crosswalk_id?: number,
) {
  const path = '/content/v1/scope_and_sequence/create_tree'
  return makeContentServiceFlowRequest(path, {
    grade,
    units,
    title,
    ...(subject_id !== undefined && { subject_id }),
    ...(attribution && { attribution }),
    ...(crosswalk_id !== undefined && { crosswalk_id }),
  })
}
